import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { userAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import Toast from '../components/Toast'
import { User, Key, Calendar, Activity, Save, Trash2, Building2, ShieldCheck } from 'lucide-react'
import MineMarkedsomraderSection from '../components/MineMarkedsomraderSection'
import MineNaceKoderSection from '../components/MineNaceKoderSection'
import MineMerkerSection from '../components/MineMerkerSection'
import { formatLeasingselskap } from '../utils/formatLeasingselskap'
import { localizeApiDetail } from '../i18n/apiError'
import { dateLocale } from '../i18n/format'
import i18n from '../i18n'

interface UserProfile {
  user_id: string
  user_name: string
  organization_id: string | null
  organization_name: string | null
}

interface ApiKeyInfo {
  key_value: string
  scope: string
  is_active: boolean
  expiry_date: string | null
  rate_limit_per_minute: number
  rate_limit_per_day: number
  created_time: string
}

interface ProfileData {
  user_profile: UserProfile
  api_key_info: ApiKeyInfo
}

// Fixed to the namespace at module level so the mount effect's fetch
// functions stay stable; it still reads the current language per call.
const tAccount = i18n.getFixedT(null, 'account')

export default function UserProfile() {
  const { t } = useTranslation('account')
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [profileData, setProfileData] = useState<ProfileData | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  // Mine Organisasjoner state
  const [orgItems, setOrgItems] = useState<Array<{ org_number: string; org_name: string | null }>>([])
  const [orgLoading, setOrgLoading] = useState(true)
  const [orgError, setOrgError] = useState<string | null>(null)
  const [orgInput, setOrgInput] = useState('')
  const [orgInputError, setOrgInputError] = useState<string | null>(null)
  const [orgSaving, setOrgSaving] = useState(false)

  useEffect(() => {
    fetchProfile()
    fetchMineOrganisasjoner()
  }, [])

  const fetchMineOrganisasjoner = async () => {
    try {
      setOrgLoading(true)
      setOrgError(null)
      const response = await userAPI.getMineOrganisasjoner()
      const data = response.data?.data || response.data
      setOrgItems(Array.isArray(data?.items) ? data.items : [])
    } catch (err: any) {
      console.error('Mine organisasjoner fetch error:', err)
      setOrgError(
        localizeApiDetail(err.response?.data?.detail) || err.message || tAccount('profile.errors.loadOrgs')
      )
    } finally {
      setOrgLoading(false)
    }
  }

  const saveOrgNumbers = async (next: string[]) => {
    try {
      setOrgSaving(true)
      const response = await userAPI.updateMineOrganisasjoner(next)
      const data = response.data?.data || response.data
      setOrgItems(Array.isArray(data?.items) ? data.items : [])
    } catch (err: any) {
      console.error('Mine organisasjoner save error:', err)
      setToast({
        message: localizeApiDetail(err.response?.data?.detail) || t('profile.errors.saveOrgs'),
        type: 'error',
      })
      throw err
    } finally {
      setOrgSaving(false)
    }
  }

  const handleAddOrgNumber = async () => {
    const parts = orgInput
      .split(/[,;\s]+/)
      .map((p) => p.trim())
      .filter(Boolean)
    if (parts.length === 0) {
      setOrgInputError(t('profile.orgs.inputEmpty'))
      return
    }
    const invalid = parts.filter((p) => !/^\d{9}$/.test(p))
    if (invalid.length > 0) {
      setOrgInputError(
        t('profile.orgs.inputInvalid', { numbers: invalid.join(', ') })
      )
      return
    }
    const existing = new Set(orgItems.map((o) => o.org_number))
    const toAdd: string[] = []
    const duplicates: string[] = []
    for (const p of parts) {
      if (existing.has(p) || toAdd.includes(p)) {
        duplicates.push(p)
      } else {
        toAdd.push(p)
      }
    }
    if (toAdd.length === 0) {
      setOrgInputError(t('profile.orgs.allDuplicates'))
      return
    }
    setOrgInputError(null)
    const next = [...orgItems.map((o) => o.org_number), ...toAdd]
    try {
      await saveOrgNumbers(next)
      setOrgInput('')
      const addedMsg =
        toAdd.length === 1
          ? t('profile.orgs.addedOne')
          : t('profile.orgs.addedMany', { n: toAdd.length })
      const dupMsg =
        duplicates.length > 0 ? t('profile.orgs.duplicatesSuffix', { n: duplicates.length }) : ''
      setToast({ message: addedMsg + dupMsg, type: 'success' })
    } catch {
      /* handled in saveOrgNumbers */
    }
  }

  const handleRemoveOrgNumber = async (orgNumber: string) => {
    const next = orgItems.map((o) => o.org_number).filter((n) => n !== orgNumber)
    try {
      await saveOrgNumbers(next)
      setToast({ message: t('profile.orgs.removed'), type: 'success' })
    } catch {
      /* handled in saveOrgNumbers */
    }
  }

  const fetchProfile = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await userAPI.getProfile()
      
      console.log('Profile API response:', response.data)
      
      // Handle flat response structure
      const data = response.data?.data || response.data
      
      // Wrap in expected structure if needed
      const profileData = {
        user_profile: data,
        api_key_info: data.api_key_info || null
      }
      
      console.log('Processed profile data:', profileData)
      setProfileData(profileData)
    } catch (err: any) {
      console.error('Profile fetch error:', err)
      console.error('Error details:', err.response?.data)
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || tAccount('profile.errors.loadProfile'))
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('profile.noExpiry')
    return new Date(dateString).toLocaleDateString(dateLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />
  if (!profileData || !profileData.user_profile) {
    return <ErrorMessage message={t('profile.errors.noProfileData')} />
  }

  const { user_profile, api_key_info } = profileData

  return (
    <div className="space-y-6">
      {/* Toast notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('profile.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('profile.subtitle')}
        </p>
      </div>

      {/* User Information */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
            <User className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {t('profile.userInfo.title')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('profile.userInfo.subtitle')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('profile.userInfo.username')}
            </label>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {user_profile.user_name}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('profile.userInfo.userId')}
            </label>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
              {user_profile.user_id}
            </p>
          </div>

          {user_profile.organization_name && (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('profile.userInfo.organisation')}
                </label>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {user_profile.organization_name}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('profile.userInfo.organisationId')}
                </label>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {user_profile.organization_id}
                </p>
              </div>
            </>
          )}

          <div className="md:col-span-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              {t('profile.userInfo.roles')}
            </label>
            <div className="mt-1">
              {user?.roles && user.roles.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {user.roles.map((role) => (
                    <span
                      key={role}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">{t('profile.userInfo.noRoles')}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <MineMarkedsomraderSection
        onToast={(message, type) => setToast({ message, type })}
      />

      <MineNaceKoderSection
        onToast={(message, type) => setToast({ message, type })}
      />

      <MineMerkerSection
        onToast={(message, type) => setToast({ message, type })}
      />

      {/* Mine Organisasjoner */}
      <div className="card p-6" data-testid="mine-org-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {t('profile.orgs.title')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('profile.orgs.subtitle')}
            </p>
          </div>
        </div>

        {orgLoading ? (
          <LoadingSpinner />
        ) : orgError ? (
          <ErrorMessage message={orgError} />
        ) : (
          <div className="space-y-4">
            {orgItems.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {orgItems.map((item) => (
                  <span
                    key={item.org_number}
                    data-testid={`mine-org-item-${item.org_number}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-lg font-medium"
                  >
                    <span>
                      {item.org_number}
                      {item.org_name ? ` — ${formatLeasingselskap(item.org_name)}` : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveOrgNumber(item.org_number)}
                      disabled={orgSaving}
                      data-testid={`mine-org-remove-${item.org_number}`}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50"
                      aria-label={t('profile.orgs.remove', { orgNumber: item.org_number })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Building2 className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400">
                  {t('profile.orgs.empty')}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('profile.orgs.inputLabel')}
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={orgInput}
                  onChange={(e) => {
                    setOrgInput(e.target.value.replace(/[^\d,;\s]/g, ''))
                    if (orgInputError) setOrgInputError(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddOrgNumber()
                    }
                  }}
                  placeholder="123456789, 987654321"
                  data-testid="mine-org-input"
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100"
                />
                <button
                  type="button"
                  onClick={handleAddOrgNumber}
                  disabled={orgSaving}
                  data-testid="mine-org-add"
                  className="btn-primary flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {orgSaving ? t('profile.orgs.saving') : t('profile.orgs.add')}
                </button>
              </div>
              {orgInputError && (
                <p
                  data-testid="mine-org-error"
                  className="mt-2 text-sm text-red-600 dark:text-red-400"
                >
                  {orgInputError}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* API Key Information */}
      {api_key_info && (
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <Key className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('profile.apiKey.title')}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('profile.apiKey.subtitle')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('profile.apiKey.accessLevel')}
              </label>
              <div className="mt-1">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                  {api_key_info.scope || 'N/A'}
                </span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('profile.apiKey.status')}
              </label>
              <div className="mt-1">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  api_key_info.is_active 
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                }`}>
                  {api_key_info.is_active ? t('profile.apiKey.active') : t('profile.apiKey.inactive')}
                </span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                {t('profile.apiKey.perMinute')}
              </label>
              <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('profile.apiKey.requests', { n: api_key_info.rate_limit_per_minute || 0 })}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                {t('profile.apiKey.perDay')}
              </label>
              <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('profile.apiKey.requests', { n: api_key_info.rate_limit_per_day?.toLocaleString('nb-NO') || '0' })}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {t('profile.apiKey.created')}
              </label>
              <p className="mt-1 text-gray-900 dark:text-gray-100">
                {formatDate(api_key_info.created_time)}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {t('profile.apiKey.expires')}
              </label>
              <p className="mt-1 text-gray-900 dark:text-gray-100">
                {formatDate(api_key_info.expiry_date)}
              </p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-border">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
              {t('profile.apiKey.key')}
            </label>
            <div className="flex items-center gap-3">
              <code className="flex-1 px-4 py-2 bg-gray-100 dark:bg-dark-bg rounded-lg text-sm font-mono text-gray-900 dark:text-gray-100 break-all">
                {api_key_info.key_value || t('profile.apiKey.notAvailable')}
              </code>
              {api_key_info.key_value && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(api_key_info.key_value)
                    setToast({ message: t('profile.apiKey.copied'), type: 'success' })
                  }}
                  className="btn-secondary whitespace-nowrap"
                >
                  {t('profile.apiKey.copy')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
